import { errorSourceLabels, httpRequestLogTypeLabels, sagaEventLabels, type ErrorHandlingSagaDetail, type HttpRequestLogHeader } from '@/dtos/ErrorHandlingSaga'
import { useGetLogData, useSetNote } from '@/hooks/useErrorHandlingSagas'
import {
  Badge, Box, Button, Flex, Heading, SimpleGrid, Spinner, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr,
} from '@chakra-ui/react'
import { Fragment, useState } from 'react'
import { ResolverDisplay } from './ResolverDisplay'
import { SagaActions } from './SagaActions'
import { SagaAssigneeSelect } from './SagaAssigneeSelect'
import { SagaWorkflowSection } from './SagaWorkflow/SagaWorkflowSection'

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString('cs-CZ') : '')

// Trvání odvozeno z časových razítek (StartedAt/CompletedAt), nikoli z uložené hodnoty.
const durationMs = (startedAt: string, completedAt: string) =>
  Math.max(0, new Date(completedAt).getTime() - new Date(startedAt).getTime())

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => {
  const primitive = typeof children === 'string' || typeof children === 'number'
  return (
    <Box>
      <Text fontSize="xs" color="gray.500">{label}</Text>
      {primitive || !children ? <Text>{children || '—'}</Text> : children}
    </Box>
  )
}

const Payload = ({ label, content }: { label: string; content?: string }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" mb={1}>{label}</Text>
    <Box
      as="pre"
      fontSize="xs"
      bg="gray.50"
      p={2}
      borderRadius="md"
      maxH="320px"
      overflow="auto"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
    >
      {content || '—'}
    </Box>
  </Box>
)

// Lazily loaded request/response payload for one "Logy" row (fetched only once the row is expanded).
const LogDataPanel = ({ logId }: { logId: number }) => {
  const { data, isLoading, isError } = useGetLogData(logId)

  if (isLoading) return <Box py={2}><Spinner size="sm" /></Box>
  if (isError || !data) return <Text py={2} color="gray.500">Data nejsou k dispozici.</Text>

  return (
    <Box display="grid" gap={3} py={2}>
      <Payload label="Request" content={data.requestContent} />
      <Payload label="Response" content={data.responseContent} />
      {data.error?.message && <Payload label="Chyba" content={data.error.message} />}
    </Box>
  )
}

// Request/response log rows (Komunikační logy). Reused both nested under a SagaAuditLog attempt and
// in the trailing "nepřiřazené" section. Tracks its own expanded-row state (log ids are globally unique).
const LogsTable = ({ logs }: { logs: HttpRequestLogHeader[] }) => {
  const [openLogId, setOpenLogId] = useState<number | null>(null)

  return (
    <Table size="sm">
      <Thead>
        <Tr>
          <Th>Začátek</Th>
          <Th>Konec</Th>
          <Th>Status</Th>
          <Th>Typ</Th>
          <Th>URI</Th>
          <Th>Data</Th>
        </Tr>
      </Thead>
      <Tbody>
        {logs.map(l => (
          <Fragment key={l.id}>
            <Tr>
              <Td whiteSpace="nowrap">{formatDate(l.requestStartTime)}</Td>
              <Td whiteSpace="nowrap">{formatDate(l.requestEndTime)}</Td>
              <Td>{l.statusCode}</Td>
              <Td>{httpRequestLogTypeLabels[l.type] ?? l.type}</Td>
              <Td maxW="360px" isTruncated title={l.requestUri}>{l.requestUri}</Td>
              <Td>
                <Button size="xs" variant="link" colorScheme="blue"
                  onClick={() => setOpenLogId(openLogId === l.id ? null : l.id)}>
                  {openLogId === l.id ? 'Skrýt' : 'Zobrazit'}
                </Button>
              </Td>
            </Tr>
            {openLogId === l.id && (
              <Tr>
                <Td colSpan={6} bg="white"><LogDataPanel logId={l.id} /></Td>
              </Tr>
            )}
          </Fragment>
        ))}
      </Tbody>
    </Table>
  )
}

export const ErrorDetail = ({ detail, reload }: { detail: ErrorHandlingSagaDetail; reload: () => void }) => {
  const setNote = useSetNote()
  const [note, setNote_] = useState(detail.note ?? '')
  const [openAttemptId, setOpenAttemptId] = useState<number | null>(null)

  const saveNote = async () => {
    await setNote.mutateAsync({ id: detail.id, note: note || null })
    reload()
  }

  return (
    <Box display="grid" gap={6} mb={16}>
      <Flex align="center" gap={4} wrap="wrap">
        <Heading size="md">Detail chyby</Heading>
        <Flex gap={2} ml="auto" wrap="wrap">
          <SagaActions ids={[detail.id]} onDone={reload} size="sm" />
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Field label="Vytvořeno">{formatDate(detail.createdAt)}</Field>
        <Field label="Typ požadavku">{detail.eventType != null ? sagaEventLabels[detail.eventType] : ''}</Field>
        <Field label="Typ zpracování">{detail.processingType}</Field>
        <Field label="Stav"><Badge>{detail.state}</Badge></Field>
        <Field label="Číslo LS">{detail.leasingContractNumber}</Field>
        <Field label="Číslo PS">{detail.contractNumber}</Field>
        <Field label="Pojišťovna (id)">{detail.insurerId}</Field>
        <Field label="InsurerOfferId">{detail.insurerOfferId}</Field>
        <Field label="Počet volání">{detail.attemptCount}</Field>
        <Field label="Datum posl. volání">{formatDate(detail.lastAttemptAt)}</Field>
        <Field label="Řešitel"><SagaAssigneeSelect ids={[detail.id]} assigneeId={detail.assigneeId} assigneeName={detail.assigneeName} onAssigned={reload} /></Field>
        <Field label="Vyřešil"><ResolverDisplay state={detail.state} resolvedById={detail.resolvedById} resolvedByName={detail.resolvedByName} /></Field>
        <Field label="Vytvořil">{detail.createdByName}</Field>
        <Field label="RequestId">{detail.requestId}</Field>
      </SimpleGrid>

      <Box>
        <Heading size="sm" mb={2}>Poznámka</Heading>
        <Textarea value={note} onChange={e => setNote_(e.target.value)} rows={3} />
        <Button mt={2} size="sm" colorScheme="blue" isLoading={setNote.isPending} onClick={saveNote}>Uložit poznámku</Button>
      </Box>

      <Box>
        <Heading size="sm" mb={2}>Průběh zpracování</Heading>
        <SagaWorkflowSection rootId={detail.id} onUpdate={reload} />
      </Box>

      <Box>
        <Heading size="sm" mb={2}>Historie volání</Heading>
        <Box maxW="100%" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Dokončeno</Th>
                <Th>Zpráva</Th>
                <Th>Výsledek</Th>
                <Th>Zdroj</Th>
                <Th>Chyba / důvod</Th>
                <Th>Trvání (ms)</Th>
                <Th>Logy</Th>
              </Tr>
            </Thead>
            <Tbody>
              {detail.attempts.map(a => (
                <Fragment key={a.id}>
                  <Tr>
                    <Td whiteSpace="nowrap">{formatDate(a.completedAt)}</Td>
                    <Td>{a.messageType}</Td>
                    <Td><Badge colorScheme={a.success ? 'green' : 'red'}>{a.success ? 'OK' : 'CHYBA'}</Badge></Td>
                    <Td>{a.errorSource != null ? errorSourceLabels[a.errorSource] : ''}</Td>
                    <Td maxW="360px" isTruncated title={a.error ?? a.reason ?? ''}>{a.error ?? a.reason}</Td>
                    <Td textAlign="right">{durationMs(a.startedAt, a.completedAt)}</Td>
                    <Td>
                      {a.logs.length > 0 ? (
                        <Button size="xs" variant="link" colorScheme="blue"
                          onClick={() => setOpenAttemptId(openAttemptId === a.id ? null : a.id)}>
                          {openAttemptId === a.id ? 'Skrýt' : `Zobrazit (${a.logs.length})`}
                        </Button>
                      ) : (
                        <Text color="gray.400">—</Text>
                      )}
                    </Td>
                  </Tr>
                  {openAttemptId === a.id && a.logs.length > 0 && (
                    <Tr>
                      <Td colSpan={7} bg="gray.50" p={0}><LogsTable logs={a.logs} /></Td>
                    </Tr>
                  )}
                </Fragment>
              ))}
              {detail.attempts.length === 0 && (
                <Tr><Td colSpan={7}><Text py={4} color="gray.500" textAlign="center">Žádné záznamy</Text></Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {detail.unmatchedLogs.length > 0 && (
        <Box>
          <Heading size="sm" mb={2}>Komunikační logy (nepřiřazené)</Heading>
          <Box maxW="100%" overflowX="auto">
            <LogsTable logs={detail.unmatchedLogs} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
